// CashCountPage.jsx - CORRIGIDO
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  Box, Card, CardContent, Typography, Grid, TextField,
  MenuItem, Chip, Stack, Paper, InputAdornment,
  Alert, CircularProgress, Divider, IconButton, Button
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { X } from "lucide-react";
import api from '../services/api'; // Using standard API service
import html2canvas from "html2canvas";
import DailySummaryView from "./DailySummaryView";

function formatMoney(v) {
  if (v === null || v === undefined) return "$0.00";
  const n = Number(v) || 0;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function todayYYYYMMDD() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const last5 = (val) => (val ? String(val).slice(-5) : "");

export default function CashCountPage() {
  const [date, setDate] = useState(todayYYYYMMDD());
  const [hotelId, setHotelId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  // === Daily Summary preview/download state ===
  const summaryRef = useRef(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [hotels, setHotels] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [quickSearch, setQuickSearch] = useState("");
  
  // Estado para controlar as vendas conferidas
  const [checkedSales, setCheckedSales] = useState(new Set());

  // Load hotels with sales for the selected date
  useEffect(() => {
    if (!date) return;

    setHotels([]);
    setHotelId("");
    setEmployees([]);
    setEmployeeId("");
    setResult(null);
    setError("");
    // Reset checked sales when filters change
    setCheckedSales(new Set());
    // Reset daily summary when date changes
    setDailySummary(null);

    const loadHotels = async () => {
      try {
        const response = await api.get("/hotels-with-sales", { 
          params: { date } 
        });
        setHotels(response.data || []);
        if ((response.data || []).length === 0) {
          setError("No hotels with sales for this date.");
        }
      } catch (err) {
        console.error("Error loading hotels:", err);
        setHotels([]);
        setError(`Error loading hotels: ${err.response?.data?.msg || err.message}`);
      }
    };

    loadHotels();
  }, [date]);

  // Load employees with sales for selected date + hotel
  useEffect(() => {
    if (!date || !hotelId) {
      setEmployees([]);
      setEmployeeId("");
      // Reset daily summary when hotel changes
      setDailySummary(null);
      return;
    }
    
    setEmployees([]);
    setEmployeeId("");
    // Reset daily summary when hotel changes
    setDailySummary(null);

    const loadEmployees = async () => {
      try {
        const response = await api.get("/employees-with-sales", { 
          params: { date, hotel_id: hotelId } 
        });
        setEmployees(response.data || []);
      } catch (err) {
        console.error("Error loading employees:", err);
        setEmployees([]);
      }
    };

    loadEmployees();
  }, [date, hotelId]);

  const handleSearch = async () => {
    if (!hotelId || !date) return;
    setLoading(true);
    setError("");
    setResult(null);
    // Reset checked sales when searching
    setCheckedSales(new Set());
    // Reset daily summary when searching
    setDailySummary(null);
    
    try {
      const params = { date, hotel_id: hotelId };
      if (employeeId) params.employee_id = employeeId;
      
      const response = await api.get("/cash-count", { params });
      setResult(response.data);
    } catch (err) {
      console.error("Error loading cash count data:", err);
      setError(err.response?.data?.msg || err.message || "Error loading data");
    } finally {
      setLoading(false);
    }
  };
  
  // === Daily Summary handlers ===
  const loadDailySummaryPreview = async () => {
    if (!date) return;
    if (!employeeId) {
      alert('Please select an employee to generate the Daily Log Sheet.');
      return;
    }
    setSummaryLoading(true);
    try {
      const res = await api.get('/daily_summary_data_admin', {
        params: { date, employee_id: employeeId }
      });
      setDailySummary(res.data);
    } catch (err) {
      console.error('Error fetching daily summary:', err);
      alert(err?.response?.data?.msg || 'Failed to generate daily summary.');
      setDailySummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const downloadDailyAsPNG = async () => {
    if (!summaryRef.current) {
      alert('Generate preview first.');
      return;
    }
    const canvas = await html2canvas(summaryRef.current, {
      scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff'
    });
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    const emp = (dailySummary?.name || 'employee').replace(/\s+/g, '_');
    const dt  = (dailySummary?.date || date).replace(/[\/]/g, '-');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `Daily_Summary_${emp}_${dt}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadDailyAsPDF = async () => {
    if (!date || !employeeId) {
      alert('Please select date and employee.');
      return;
    }
    try {
      const response = await api.get('/generate_daily_summary_pdf_admin', {
        params: { date, employee_id: employeeId },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const emp = (dailySummary?.name || 'employee').replace(/\s+/g, '_');
      const dt  = (dailySummary?.date || date).replace(/[\/]/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily_summary_${emp}_${dt}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert(err?.response?.data?.msg || 'Failed to download PDF.');
    }
  };

  // Auto fetch on filters change
  useEffect(() => {
    if (hotelId && date) handleSearch();
    // Reset daily summary when employee changes
    setDailySummary(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, date, employeeId]);

  // Function to toggle checked status of a sale
  const toggleSaleCheck = (saleId) => {
    const newCheckedSales = new Set(checkedSales);
    if (newCheckedSales.has(saleId)) {
      newCheckedSales.delete(saleId);
    } else {
      newCheckedSales.add(saleId);
    }
    setCheckedSales(newCheckedSales);
  };

  // Filtered sales
  const filteredSales = useMemo(() => {
    if (!result?.sales) return [];
    const q = (quickSearch || "").toLowerCase().trim();
    if (!q) return result.sales;
    return result.sales.filter((s) => {
      const idStr = String(s.sale_id || "");
      const localId = String(s.local_sale_id || "");
      const time = (s.time_local || "").toLowerCase();
      const emp = (s.employee?.name || "").toLowerCase();
      const pm = (s.payment_method || "").toLowerCase();
      return (
        idStr.includes(q) ||
        localId.includes(q) ||
        time.includes(q) ||
        emp.includes(q) ||
        pm.includes(q)
      );
    });
  }, [result, quickSearch]);

  const expected = result?.totals?.expected_deposit || {
    cash: 0,
    room_charge: 0,
    credit_card: 0,
    square: 0,
  };
  const expectedTotal = Number(result?.totals?.expected_deposit_total || 0);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1000, mx: "auto", pb: 4 }}>
      {/* Filters - Centered with modern styling */}
      <Box sx={{ 
        px: 5, py: 2, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        alignItems: 'center', 
        gap: 1, 
        maxWidth: 1200, 
        margin: '0 auto',
        mb: 2, backgroundColor: '#fff',  borderRadius: 2, 
      }}>
        <TextField
          fullWidth
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ 
            flex: 2,
            backgroundColor: '#fff', 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': { 
              borderRadius: 2,
              '& fieldset': { 
                borderColor: '#e0e0e0' 
              } 
            }
          }}
        />
        <TextField
          select
          fullWidth
          label="Hotel"
          value={hotelId}
          onChange={(e) => setHotelId(e.target.value)}
          size="small"
          disabled={hotels.length === 0}
          helperText={
            hotels.length === 0
              ? "No hotels with sales on this date"
              : `${hotels.length} hotel(s) with sales`
          }
          sx={{ 
            flex: 3,
            backgroundColor: '#fff', 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': { 
              borderRadius: 2,
              '& fieldset': { 
                borderColor: '#e0e0e0' 
              } 
            }
          }}
        >
          {hotels.map((h) => (
            <MenuItem key={h.id} value={h.id}>
              {h.name} ({h.sales_count} sales)
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Employee (optional)"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          size="small"
          disabled={!hotelId || employees.length === 0}
          helperText={
            !hotelId
              ? "Select a hotel first"
              : employees.length === 0
              ? "No employees with sales"
              : `${employees.length} employee(s)`
          }
          sx={{ 
            flex: 3,
            backgroundColor: '#fff', 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': { 
              borderRadius: 2,
              '& fieldset': { 
                borderColor: '#e0e0e0' 
              } 
            }
          }}
        >
          <MenuItem value="">All</MenuItem>
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.name} ({emp.sales_count} sales)
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Quick search with modern styling */}
      {result && (
        <Box sx={{ 
          pb: 1, 
          display: 'flex', 
          justifyContent: 'center',
          maxWidth: 1000, 
          margin: '0 auto',
          mb: 2
        }}>
          <TextField
            size='small' 
            placeholder="Search by sale ID, local ID, time, employee, or method..."
            value={quickSearch} 
            onChange={(e) => setQuickSearch(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ 
              backgroundColor: '#fff', 
              borderRadius: 5, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              '& .MuiOutlinedInput-root': { 
                borderRadius: 5,
                '& fieldset': { 
                  border: 'none' 
                } 
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={18} color="#757575" />
                </InputAdornment>
              ),
              endAdornment: quickSearch ? (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={() => setQuickSearch('')} 
                    size="small" 
                    edge="end"
                    sx={{ mr: -0.5 }}
                  >
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Paper sx={{ p: 3, textAlign: "center", mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography>Loading...</Typography>
        </Paper>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && (
        <Box>
          {/* Sales list - Centered grid */}
          {filteredSales.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography>No sales for the selected filters.</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{ maxWidth: 1200, width: '100%' }}>
                <Grid container spacing={2} justifyContent="center">
                  {filteredSales.map((sale) => {
                    const isChecked = checkedSales.has(sale.sale_id);
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={sale.sale_id}>
                        <Paper 
                          sx={{ 
                            p: 2, 
                            bgcolor: isChecked ? "success.50" : "grey.50",
                            border: "2px solid",
                            borderColor: isChecked ? "success.300" : "grey.300",
                            borderRadius: 2,
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            "&:hover": { 
                              bgcolor: isChecked ? "success.100" : "grey.100",
                              borderColor: isChecked ? "success.400" : "grey.400",
                              transform: 'scale(1.02)'
                            }
                          }}
                          onClick={() => toggleSaleCheck(sale.sale_id)}
                        >
                          {/* Visual checkbox in top right corner */}
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            {isChecked ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.5rem' }} />
                            ) : (
                              <CheckCircleOutlineIcon sx={{ color: 'grey.400', fontSize: '1.5rem' }} />
                            )}
                          </Box>

                          {/* Header */}
                          <Box sx={{ textAlign: "center", mb: 1, borderBottom: "1px dashed", borderColor: "grey.400", pb: 1, pr: 4 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                              HAIRWRAP & TATTOOS
                            </Typography>
                          </Box>

                          {/* Sale ID */}
                          <Box sx={{ mb: 1 }}>
                            {sale.local_sale_id ? (
                              <>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                                  Sale ID: {sale.sale_id}
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                                  Local ID: {last5(sale.local_sale_id)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                                Sale ID: {sale.sale_id}
                              </Typography>
                            )}
                          </Box>

                          {/* Employee and Time */}
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              Employee: {sale.employee?.name || "N/A"}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              Time: {sale.time_local}
                            </Typography>
                          </Box>

                          {/* Payment Method */}
                          <Box sx={{ mb: 1 }}>
                            <Chip
                              size="small"
                              label={sale.payment_method}
                              color={sale.payment_method === "cash" ? "success" : "default"}
                              sx={{ fontSize: "0.7rem", height: "20px" }}
                            />
                          </Box>

                          {/* Divider */}
                          <Box sx={{ borderBottom: "1px dashed", borderColor: "grey.400", mb: 1 }}></Box>

                          {/* Items */}
                          <Box sx={{ mb: 1 }}>
                            {sale.items && sale.items.map((item, idx) => (
                              <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 600 }}>
                                    {item.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem", color: "text.secondary" }}>
                                    {item.qty}x {formatMoney(item.unit_price)}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 600 }}>
                                  {formatMoney(item.subtotal)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          {/* Divider */}
                          <Box sx={{ borderBottom: "1px dashed", borderColor: "grey.400", mb: 1 }}></Box>

                          {/* Totals */}
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                                SUBTOTAL:
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", fontWeight: 600 }}>
                                {formatMoney(sale.net_no_tip)}
                              </Typography>
                            </Box>
                            
                            {sale.tip > 0 && (
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                                  TIP:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", fontWeight: 600 }}>
                                  {formatMoney(sale.tip)}
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5, borderTop: "1px solid", borderColor: "grey.400" }}>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", fontWeight: 700 }}>
                                TOTAL:
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", fontWeight: 700, color: isChecked ? "success.main" : "primary.main" }}>
                                {formatMoney(sale.total_amount)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Bottom */}
                          <Box sx={{ textAlign: "center", mt: 1, pt: 1, borderTop: "1px dashed", borderColor: "grey.400" }}>
                            <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem", color: "text.secondary" }}>
                              Thank you for your visit!
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          )}

          {/* Payment Method Totals - Centered */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ borderRadius: 2, maxWidth: 1000, width: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
                  Payment Total
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }} justifyContent="center">
                  {/* Cash */}
                  {result.totals?.by_method?.cash?.net_no_tip > 0 && (
                    <Grid item xs={6} md={2}>
                      <Paper sx={{ p: 2, textAlign: "center", bgcolor: "success.50", border: "1px solid", borderColor: "success.200" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Cash
                        </Typography>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                          {formatMoney(result.totals.by_method.cash.net_no_tip)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Room Charge */}
                  {result.totals?.by_method?.room_charge?.net_no_tip > 0 && (
                    <Grid item xs={6} md={2}>
                      <Paper sx={{ p: 2, textAlign: "center", bgcolor: "info.50", border: "1px solid", borderColor: "info.200" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Room Charge
                        </Typography>
                        <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                          {formatMoney(result.totals.by_method.room_charge.net_no_tip)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Credit Card */}
                  {result.totals?.by_method?.credit_card?.net_no_tip > 0 && (
                    <Grid item xs={6} md={2}>
                      <Paper sx={{ p: 2, textAlign: "center", bgcolor: "warning.50", border: "1px solid", borderColor: "warning.200" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Credit Card
                        </Typography>
                        <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                          {formatMoney(result.totals.by_method.credit_card.net_no_tip)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Square */}
                  {result.totals?.by_method?.square?.net_no_tip > 0 && (
                    <Grid item xs={6} md={2}>
                      <Paper sx={{ p: 2, textAlign: "center", bgcolor: "secondary.50", border: "1px solid", borderColor: "secondary.200" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Square
                        </Typography>
                        <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700 }}>
                          {formatMoney(result.totals.by_method.square.net_no_tip)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Total */}
                  <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.50", border: "2px solid", borderColor: "primary.300" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Total
                      </Typography>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                        {formatMoney(result.totals?.net_no_tip)}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Gratuity */}
                  <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "purple.50", border: "1px solid", borderColor: "purple.200" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Gratuity
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#7b1fa2" }}>
                        {formatMoney(result.totals?.tip_total)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Card sx={{ borderRadius: 2, maxWidth: 1000, width: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
                  Daily Log Sheet
                </Typography>
                <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                  <Grid item>
                    <Button
                      variant="contained"
                      onClick={loadDailySummaryPreview}
                      disabled={!employeeId || summaryLoading}
                    >
                      {summaryLoading ? 'Loading...' : 'Preview'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={downloadDailyAsPDF}
                      disabled={!employeeId}
                    >
                      Download PDF
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={downloadDailyAsPNG}
                      disabled={!dailySummary}
                    >
                      Download PNG
                    </Button>
                  </Grid>
                </Grid>

                {dailySummary ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box ref={summaryRef} sx={{ border: '1px solid #ddd', p: 1, bgcolor: '#fff' }}>
                      <DailySummaryView summaryData={dailySummary} />
                    </Box>
                  </Box>
                ) : (
                  <Typography align="center" color="text.secondary">
                    Please select an employee and click "Preview".
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
}